/**
 * Server state lives in TanStack Query. Query keys are hierarchical so a
 * mutation can invalidate exactly what it touched:
 *
 *   ["orgs"]                                   organizations list
 *   ["org", org]                               everything inside an org
 *   ["org", org, "apps"]                       application list
 *   ["org", org, "app", app]                   everything inside an app
 *   ["org", org, "app", app, "notifications"]  …and so on
 */
import { queryOptions } from "@tanstack/react-query";
import type { NotificationListQuery, SubscriptionStatus } from "@nudgebox/contracts";
import { api } from "./api.ts";
import { authClient } from "./auth.ts";

export const queryKeys = {
  session: ["session"] as const,
  passkeys: ["passkeys"] as const,
  orgs: ["orgs"] as const,
  org: (org: string) => ["org", org] as const,
  apps: (org: string) => ["org", org, "apps"] as const,
  app: (org: string, app: string) => ["org", org, "app", app] as const,
  channels: (org: string, app: string) => ["org", org, "app", app, "channels"] as const,
  notifications: (org: string, app: string) => ["org", org, "app", app, "notifications"] as const,
  notificationList: (
    org: string,
    app: string,
    filters: { view: NonNullable<NotificationListQuery["view"]>; from?: string | undefined; to?: string | undefined },
  ) => ["org", org, "app", app, "notifications", filters] as const,
  notification: (org: string, app: string, id: string) => ["org", org, "app", app, "notifications", id] as const,
  subscribers: (
    org: string,
    app: string,
    filters: { status?: SubscriptionStatus | undefined; channel?: string | undefined },
  ) => ["org", org, "app", app, "subscribers", filters] as const,
  apiKeys: (org: string) => ["org", org, "api-keys"] as const,
  billing: (org: string) => ["org", org, "billing"] as const,
  members: (org: string) => ["org", org, "members"] as const,
  invitations: (org: string) => ["org", org, "invitations"] as const,
  receivedInvitation: (id: string) => ["invitation", id] as const,
  overview: (org: string) => ["org", org, "overview"] as const,
  publicApp: (slug: string) => ["public", "app", slug] as const,
};

export const sessionQuery = queryOptions({
  queryKey: queryKeys.session,
  queryFn: async () => {
    const { data } = await authClient.getSession();
    return data ?? null;
  },
  staleTime: 60_000,
});

export const passkeysQuery = queryOptions({
  queryKey: queryKeys.passkeys,
  queryFn: async () => {
    const { data, error } = await authClient.passkey.listUserPasskeys();
    if (error) throw new Error(error.message ?? "Could not load passkeys.");
    return data;
  },
});

export const organizationsQuery = queryOptions({
  queryKey: queryKeys.orgs,
  queryFn: () => api.organizations.list(),
});

export const organizationQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.org(org), queryFn: () => api.organizations.get({ org }) });

export const overviewQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.overview(org), queryFn: () => api.organizations.overview({ org }) });

export const applicationsQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.apps(org), queryFn: () => api.applications.list({ org }) });

export const applicationQuery = (org: string, app: string) =>
  queryOptions({ queryKey: queryKeys.app(org, app), queryFn: () => api.applications.get({ org, app }) });

export const channelsQuery = (org: string, app: string) =>
  queryOptions({ queryKey: queryKeys.channels(org, app), queryFn: () => api.channels.list({ org, app }) });

export const notificationQuery = (org: string, app: string, notificationId: string) =>
  queryOptions({
    queryKey: queryKeys.notification(org, app, notificationId),
    queryFn: () => api.notifications.get({ org, app, notificationId }),
    // Poll while fan-out is in flight.
    refetchInterval: (query) =>
      query.state.data && ["queued", "sending"].includes(query.state.data.status) ? 1500 : false,
  });

export const deliveriesQuery = (org: string, app: string, notificationId: string) =>
  queryOptions({
    queryKey: [...queryKeys.notification(org, app, notificationId), "deliveries"],
    queryFn: () => api.notifications.deliveries({ org, app, notificationId }),
  });

export const apiKeysQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.apiKeys(org), queryFn: () => api.apiKeys.list({ org }) });

export const billingQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.billing(org), queryFn: () => api.billing.get({ org }) });

export const membersQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.members(org), queryFn: () => api.organizations.members({ org }) });

export const invitationsQuery = (org: string) =>
  queryOptions({ queryKey: queryKeys.invitations(org), queryFn: () => api.invitations.list({ org }) });

export const receivedInvitationQuery = (invitationId: string) =>
  queryOptions({
    queryKey: queryKeys.receivedInvitation(invitationId),
    queryFn: () => api.invitations.received({ invitationId }),
    // A missing invitation is an answer, not a blip worth retrying.
    retry: false,
  });

export const publicApplicationQuery = (slug: string) =>
  queryOptions({ queryKey: queryKeys.publicApp(slug), queryFn: () => api.publicApplications.get({ slug }) });
